'use client';

import React from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import {
	FileUploadStatus,
	FileUploadStatusResponse,
	FileUploadValidationErrorMessage,
	UpdateAction,
} from '@types';
import { cn, FileUploadItem, formatBytes } from '@utils';

import {
	Cancel,
	CTAButton,
	Delete,
	Document,
	DocumentError,
	ErrorFilled,
	errorMessageStyle,
	FieldSuccess,
	iconStyle,
	LoadingSpinner,
	Refresh,
} from '@/componentsQGDS';

/**
 * Props for the UploadedFileList component.
 */
interface UploadedFileListProps {
	/**
	 * Array of files to be displayed in the list.
	 */
	files: FileUploadItem[];
	/**
	 * Boolean indicating whether the component is disabled.
	 */
	isDisabled: boolean;
	/**
	 * Function to remove a file from the list, given its index.
	 */
	handleRemove: (index: number) => void;
	/**
	 * Function to restore a file from the list, given its index.
	 */
	handleRestore: (file: FileUploadItem) => void;
	/**
	 * Function to handle file upload
	 * @param file - The file to be uploaded
	 * @param setUploadingFiles - Function to update the state of uploading files
	 * @returns A promise that resolves when the upload is complete
	 */
	uploadFileMutation: UseMutationResult<FileUploadStatusResponse, unknown, File, unknown>;
	/**
	 * Function to update the status of a file
	 * @param fileName - The name of the file
	 * @param status - The new status of the file
	 */
	updateFileUploadStatus: (fileName: string, status: FileUploadStatusResponse) => void;
}

export const UploadedFileList: React.FC<UploadedFileListProps> = ({
	files,
	isDisabled,
	handleRemove,
	handleRestore,
	uploadFileMutation,
	updateFileUploadStatus,
}) => {
	const iconColorClassName = 'text-light-action-secondary';

	// Helpers
	const handleRetry = async (file: FileUploadItem) => {
		if (file.file) {
			updateFileUploadStatus(file.file.name, {
				status: FileUploadStatus.REQUESTED,
				fileuri: null,
				contactid: null,
			});
			uploadFileMutation.mutate(file.file);
		}
	};

	// Layout
	return (
		<div className="flex flex-col gap-4" role="list" aria-label="Uploaded files">
			{files.map((file, index) => {
				const fileName = file.file?.name ?? file.filename ?? '';
				const uploadInProgress = file.uploadStatus?.status === FileUploadStatus.REQUESTED;
				const uploadedSuccessFilledful = file.uploadStatus?.status === FileUploadStatus.CLEAN;
				const fileSize = file.file ? ` - ${formatBytes(file.file.size)}` : '';
				const toBeRemoved = file.action === UpdateAction.REMOVE;
				const restored = file.uploadStatus?.status === FileUploadStatus.RESTORED;
				const isMalwareError = file.uploadStatus?.status === FileUploadStatus.DIRTY;

				const hasError = !!file.error || file.uploadStatus?.status === FileUploadStatus.FAILED;
				const isApiError =
					hasError &&
					!isMalwareError &&
					!Object.values(FileUploadValidationErrorMessage).includes(
						file.error as FileUploadValidationErrorMessage,
					);

				const renderFileStatus = (file: FileUploadItem) => {
					if (hasError) {
						return (
							<p
								className={cn(errorMessageStyle({ theme: 'light' }))}
								title={`${file.error}${fileSize}`}
							>
								<ErrorFilled className={iconStyle({ theme: 'light', isInvalid: true })} />
								{file.error === FileUploadValidationErrorMessage.INVALID_FILE_FORMAT
									? `${file.error} - ${file.file?.name.split('.').pop()}`
									: `${file.error}${fileSize}`}
							</p>
						);
					} else if (toBeRemoved) {
						return <small className="text-light-text-lighter">Marked for removal</small>;
					} else if (uploadInProgress) {
						return <small className="text-light-text-lighter">Uploading...</small>;
					} else if (uploadedSuccessFilledful || restored) {
						return (
							<FieldSuccess title={`Upload complete${fileSize}`}>
								Upload complete{fileSize}
							</FieldSuccess>
						);
					} else {
						return null;
					}
				};

				return (
					<div
						key={index}
						className={cn([
							'flex flex-1 flex-col items-start gap-4 overflow-hidden p-4 lg:p-6',
							'md:flex-row md:items-center md:justify-between',
							'border-neutral-lighter bg-neutral-lightest rounded-xs border-2',
							`${uploadInProgress && 'bg-neutral-white border-dashed'}`,
							`${(hasError || toBeRemoved) && 'border-status-error bg-status-error-lightest'}`,
						])}
						role="listitem"
						aria-labelledby={`file-name-${index}`}
						aria-describedby={`file-status-${index}`}
					>
						<div className="flex min-w-0 flex-1 flex-row items-center gap-4">
							{/* File type icon & loading spinner */}
							{uploadInProgress ? (
								<LoadingSpinner />
							) : hasError || toBeRemoved ? (
								<DocumentError
									className={cn(iconStyle({ theme: 'light', isInvalid: true }), 'size-icon-lg!')}
								/>
							) : (
								<Document className={iconColorClassName} />
							)}
							{/* File title & file status information */}
							<div className="flex min-w-0 flex-col gap-2">
								<h6 id={`file-name-${index}`} className="truncate" title={fileName}>
									{fileName}
								</h6>
								<div id={`file-status-${index}`}>{renderFileStatus(file)}</div>
							</div>
						</div>
						{/* File actions */}
						<div className="flex flex-wrap gap-4">
							{isApiError && (
								<CTAButton
									onPress={() => handleRemove(index)}
									leftIcon={<Delete className={`${iconColorClassName} size-icon-md`} />}
								>
									Remove
								</CTAButton>
							)}
							<CTAButton
								isDisabled={isDisabled}
								onPress={() => {
									if (isApiError) {
										handleRetry(file);
									} else if (toBeRemoved) {
										handleRestore(file);
									} else {
										handleRemove(index);
									}
								}}
								aria-label={
									uploadInProgress
										? `Cancel upload of ${file.file?.name}`
										: isApiError
											? `Retry upload of ${file.file?.name}`
											: `Remove ${file.file?.name}`
								}
							>
								{uploadInProgress ? (
									<>
										<Cancel className={`${iconColorClassName} size-icon-sm`} />
										Cancel
									</>
								) : isApiError ? (
									<>
										<Refresh className={`${iconColorClassName} size-icon-md`} />
										Retry
									</>
								) : toBeRemoved ? (
									<>
										<Refresh className={`${iconColorClassName} size-icon-md`} />
										Restore
									</>
								) : (
									<>
										<Delete className={`${iconColorClassName} size-icon-md`} />
										Remove
									</>
								)}
							</CTAButton>
						</div>
					</div>
				);
			})}
		</div>
	);
};
